import { useState, useEffect, useCallback } from 'react';
import { websiteSettingsAPI } from '../services/postgresService';

// Default theme values
const defaultTheme = {
  coverImages: {
    hero: "/assets/images/hero-bg.jpg",
    heroCard: "/assets/images/sapiens.svg",
    about: "/assets/images/about-bg.jpg",
    donate: "/assets/images/donate-bg.png",
    courses: "/assets/images/courses-bg.jpg",
    contact: "/assets/images/contact-bg.jpg",
    events: "/assets/images/hero-bg.jpg",
  },
  logo: "/assets/images/logo.jpg",
  colors: {
    primary: "#1F7D53",
    secondary: "#255F38",
    accent: "#27391C",
    dark: "#18230F",
    light: "#FFFFFF",
  }
};

export const useSettings = () => {
  const [theme, setTheme] = useState(defaultTheme);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to convert relative paths to full URLs for display
  const ensureFullUrl = (url) => {
    if (!url) return url;
    // Normalize legacy logo paths that point to non-existent .png files
    if (url === '/assets/images/logo.png' || url === '/assets/logo.png' || url === '/logo.png') {
      return '/assets/images/logo.jpg';
    }
    // If already a full URL, return as-is
    if (url.startsWith('http')) return url;
    // If relative path, return as-is (relative to current domain)
    if (url.startsWith('/')) return url;
    // If just a filename, assume it's in /uploads
    return `/uploads/${url}`;
  };

  // Load settings from PostgreSQL on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const settings = await websiteSettingsAPI.getAllSettings();
        
        if (settings && Object.keys(settings).length > 0) {
          // Parse settings from database
          const newTheme = { ...defaultTheme };
          
          // Parse cover images
          Object.keys(defaultTheme.coverImages).forEach(key => {
            const dbKey = `${key}_bg`;
            if (settings[dbKey]) {
              newTheme.coverImages[key] = ensureFullUrl(settings[dbKey]);
            }
          });
          
          // Ensure events background is included
          if (!newTheme.coverImages.events && settings.events_bg) {
            newTheme.coverImages.events = ensureFullUrl(settings.events_bg);
          }
          
          // Parse logo
          if (settings.logo_url) {
            newTheme.logo = ensureFullUrl(settings.logo_url);
          }
          
          // Parse colors
          if (settings.primary_color) newTheme.colors.primary = settings.primary_color;
          if (settings.secondary_color) newTheme.colors.secondary = settings.secondary_color;
          if (settings.accent_color) newTheme.colors.accent = settings.accent_color;
          if (settings.dark_color) newTheme.colors.dark = settings.dark_color;
          if (settings.light_color) newTheme.colors.light = settings.light_color;
          
          setTheme(newTheme);
        }
        setError(null);
      } catch (err) {
        console.error("Error loading settings:", err);
        setError(err.message);
        // Keep default theme on error
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  // Update theme and save to PostgreSQL
  const updateTheme = useCallback(async (newThemeData) => {
    try {
      const updatedTheme = {
        ...theme,
        ...newThemeData
      };

      // Prepare settings object for database
      const settingsToUpdate = {};

      // Update cover images
      if (newThemeData.coverImages) {
        Object.entries(newThemeData.coverImages).forEach(([key, value]) => {
          settingsToUpdate[`${key}_bg`] = value;
        });
      }

      // Update logo
      if (newThemeData.logo) {
        settingsToUpdate.logo_url = newThemeData.logo;
      }

      // Update colors
      if (newThemeData.colors) {
        if (newThemeData.colors.primary) settingsToUpdate.primary_color = newThemeData.colors.primary;
        if (newThemeData.colors.secondary) settingsToUpdate.secondary_color = newThemeData.colors.secondary;
        if (newThemeData.colors.accent) settingsToUpdate.accent_color = newThemeData.colors.accent;
        if (newThemeData.colors.dark) settingsToUpdate.dark_color = newThemeData.colors.dark;
        if (newThemeData.colors.light) settingsToUpdate.light_color = newThemeData.colors.light;
      }

      // Save to database FIRST before updating local state
      if (Object.keys(settingsToUpdate).length > 0) {
        console.log('💾 Saving settings to database:', settingsToUpdate);
        await websiteSettingsAPI.setMultipleSettings(settingsToUpdate);
        console.log('✅ Settings saved successfully to database');
      }

      // Ensure URLs are full URLs in local state
      const themeWithFullUrls = {
        ...updatedTheme,
        logo: updatedTheme.logo ? ensureFullUrl(updatedTheme.logo) : updatedTheme.logo,
        coverImages: {
          ...updatedTheme.coverImages,
          heroCard: ensureFullUrl(updatedTheme.coverImages?.heroCard),
          hero: ensureFullUrl(updatedTheme.coverImages?.hero),
          about: ensureFullUrl(updatedTheme.coverImages?.about),
          courses: ensureFullUrl(updatedTheme.coverImages?.courses),
          events: ensureFullUrl(updatedTheme.coverImages?.events),
          donate: ensureFullUrl(updatedTheme.coverImages?.donate),
          contact: ensureFullUrl(updatedTheme.coverImages?.contact),
        }
      };

      // Update local state AFTER successful database save
      setTheme(themeWithFullUrls);
      setError(null);
      
      // Wait a moment then reload settings from database to verify they were saved
      console.log('🔄 Verifying settings from database...');
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Clear cache before fetching fresh data
      const { clearCache } = require('../config/postgres');
      clearCache('/api/settings');
      
      const verifySettings = await websiteSettingsAPI.getAllSettings();
      console.log('✅ Verified settings from database:', verifySettings);
      
      // Update local state with verified data
      if (verifySettings && Object.keys(verifySettings).length > 0) {
        const verifiedTheme = { ...defaultTheme };
        
        // Parse cover images
        Object.keys(defaultTheme.coverImages).forEach(key => {
          const dbKey = `${key}_bg`;
          if (verifySettings[dbKey]) {
            verifiedTheme.coverImages[key] = ensureFullUrl(verifySettings[dbKey]);
          }
        });
        
        // Parse logo
        if (verifySettings.logo_url) {
          verifiedTheme.logo = ensureFullUrl(verifySettings.logo_url);
        }
        
        // Parse colors
        if (verifySettings.primary_color) verifiedTheme.colors.primary = verifySettings.primary_color;
        if (verifySettings.secondary_color) verifiedTheme.colors.secondary = verifySettings.secondary_color;
        if (verifySettings.accent_color) verifiedTheme.colors.accent = verifySettings.accent_color;
        if (verifySettings.dark_color) verifiedTheme.colors.dark = verifySettings.dark_color;
        if (verifySettings.light_color) verifiedTheme.colors.light = verifySettings.light_color;
        
        setTheme(verifiedTheme);
        console.log('✅ Theme updated with verified data:', verifiedTheme);
      }
      
      return themeWithFullUrls;
    } catch (err) {
      console.error("❌ Error updating settings:", err);
      setError(err.message);
      throw err;
    }
  }, [theme]);

  // Update a single setting
  const updateSetting = useCallback(async (key, value) => {
    try {
      await websiteSettingsAPI.setSetting(key, value);
      setError(null);
      return true;
    } catch (err) {
      console.error(`Error updating setting ${key}:`, err);
      setError(err.message);
      throw err;
    }
  }, []);

  return { 
    theme, 
    updateTheme, 
    updateSetting,
    loading, 
    error 
  };
};
